/**
 * Filter Data Transformations
 * Centralized logic for transforming filter data between server and UI formats
 * Extracted from filterConfig.js to eliminate duplication
 */

import { getAlertTypeText } from '../../../utils/alertBadgeHelpers';

/**
 * Filter transformation configurations
 * Maps how different filter types should be transformed and displayed
 */
export const FILTER_TRANSFORMATIONS = {
  // Standard text-based filters
  text: {
    transform: (values) => values.map(value => ({
      text: value,
      value: value
    })),
    validate: (value) => typeof value === 'string' && value.length > 0,
    sort: (a, b) => a.text.localeCompare(b.text)
  },

  // Numeric filters
  numeric: {
    transform: (values) => values.map(value => ({
      text: value.toString(),
      value: value
    })),
    validate: (value) => !isNaN(value) && value !== null && value !== undefined,
    sort: (a, b) => Number(a.value) - Number(b.value)
  },

  // Date-based filters
  date: {
    transform: (values) => values.map(value => ({
      text: new Date(value).toLocaleDateString(),
      value: value
    })),
    validate: (value) => !isNaN(Date.parse(value)),
    sort: (a, b) => new Date(a.value) - new Date(b.value)
  },

  // Boolean filters
  boolean: {
    transform: (values, t) => values.map(value => ({
      text: t ? t(value ? 'yes' : 'no') : (value ? 'Yes' : 'No'),
      value: value
    })),
    validate: (value) => typeof value === 'boolean',
    sort: (a, b) => b.value - a.value // true first
  },


  // Machine IDs with intelligent sorting
  machineId: {
    transform: (values) => values.map(value => ({
      text: value,
      value: value,
      numericPart: extractNumericPart(value)
    })),
    validate: (value) => typeof value === 'string' && value.length > 0,
    sort: (a, b) => {
      // Sort by numeric part if available, otherwise alphabetically
      if (a.numericPart !== null && b.numericPart !== null) {
        return a.numericPart - b.numericPart;
      }
      return a.text.localeCompare(b.text);
    }
  },

  // Products with id and name objects
  products: {
    transform: (values) => values.map(product => ({
      text: product.name,
      value: product.id, // Use ID as the filter value
      productId: product.id,
      productName: product.name
    })),
    validate: (value) => value && typeof value === 'object' && value.id && value.name,
    sort: (a, b) => a.text.localeCompare(b.text)
  },
  
  // Routes with id and display name
  routes: {
    transform: (values) => values.map(route => {
      if (route && typeof route === 'object') {
        const id = route.id ?? route.value ?? route.name;
        const label = route.name || route.code || route.label || String(id);
        return {
          text: label,
          value: String(id)
        };
      }
      // Fallback for string values
      return {
        text: String(route),
        value: String(route)
      };
    }),
    validate: (value) => {
      if (value && typeof value === 'object') {
        return value.id !== undefined || value.name !== undefined || value.code !== undefined;
      }
      return typeof value === 'string' && value.length > 0;
    },
    sort: (a, b) => a.text.localeCompare(b.text)
  },

  // Alert types with human-readable text
  alertType: {
    transform: (values, t) => values.map(type => ({
      text: getAlertTypeText(type, t),
      value: type
    })),
    validate: (value) => typeof value === 'string' && value.length > 0,
    sort: (a, b) => a.text.localeCompare(b.text)
  },

  // Status values with human-readable text
  status: {
    transform: (values, t) => values.map(status => ({
      text: getStatusText(status, t),
      value: status
    })),
    validate: (value) => typeof value === 'string' && value.length > 0,
    sort: (a, b) => {
      // Sort by priority: pending, under_review, resolved, not_apply
      const priority = { 'pending': 1, 'under_review': 2, 'resolved': 3, 'not_apply': 4 };
      const aPriority = priority[a.value] || 999;
      const bPriority = priority[b.value] || 999;
      return aPriority - bPriority;
    }
  }
};

// Helper function for status text transformation
const getStatusText = (status, t) => {
  switch (status) {
    case 'resolved':
      return t ? t('resolved') : 'Resolved';
    case 'under_review':
      return t ? t('underReview') : 'Under Review';
    case 'not_apply':
      return t ? t('notBroken') : 'Working';
    case 'pending':
    default:
      return t ? t('pending') : 'Pending';
  }
};

/**
 * Extract numeric part from machine ID for intelligent sorting
 * @param {string} machineId - Machine identifier
 * @returns {number|null} Numeric part or null
 */
const extractNumericPart = (machineId) => {
  const match = machineId.match(/(\d+)/);
  return match ? parseInt(match[1], 10) : null;
};

/**
 * Transform server filter values to UI format
 * @param {Array} values - Raw server values
 * @param {string} type - Filter type
 * @param {Function} t - Translation function
 * @returns {Array} Transformed filter options
 */
export const transformFilterValues = (values, type = 'text', t) => {
  if (!values || !Array.isArray(values)) {
    return [];
  }

  // Filter out invalid values
  const validValues = values.filter(value => {
    const transformation = FILTER_TRANSFORMATIONS[type];
    return transformation ? transformation.validate(value) : true;
  });

  // Transform values
  const transformation = FILTER_TRANSFORMATIONS[type] || FILTER_TRANSFORMATIONS.text;
  const transformedValues = transformation.transform(validValues, t);

  // Sort values
  return transformedValues.sort(transformation.sort);
};

/**
 * Create filter options for dropdown components
 * @param {Array} serverValues - Raw server values
 * @param {string} filterKey - Filter key for type detection
 * @param {Function} t - Translation function
 * @returns {Array} Filter options ready for dropdown
 */
export const createFilterOptions = (serverValues, filterKey, t) => {
  // Detect filter type based on key
  const filterType = detectFilterType(filterKey, serverValues);
  
  // Transform values
  const transformedValues = transformFilterValues(serverValues, filterType, t);
  
  // Add metadata
  return transformedValues.map(value => ({
    ...value,
    filterKey,
    filterType
  }));
};

/**
 * Detect filter type based on key and sample values
 * @param {string} filterKey - Filter key
 * @param {Array} sampleValues - Sample values for analysis
 * @returns {string} Detected filter type
 */
export const detectFilterType = (filterKey, sampleValues = []) => {
  // Explicit type mappings
  const typeMap = {
    'machineId': 'machineId',
    'machine_id': 'machineId',
    'weeks_since': 'numeric',
    'weeksSince': 'numeric',
    'revenue_loss': 'numeric',
    'created_at': 'date',
    'updated_at': 'date',
    'resolution_date': 'date',
    'technical_problem': 'boolean',
    'productName': 'products', // Maps to products filter type
    'products': 'products', // Direct mapping
    'alertType': 'alertType',
    'status': 'status',
    'routes': 'routes',
    'route': 'routes'
  };

  if (typeMap[filterKey]) {
    return typeMap[filterKey];
  }

  // Auto-detect based on sample values
  if (sampleValues.length > 0) {
    const firstValue = sampleValues[0];
    
    if (typeof firstValue === 'boolean') {
      return 'boolean';
    }
    
    if (typeof firstValue === 'number') {
      return 'numeric';
    }
    
    if (typeof firstValue === 'string') {
      // Check if it's a date
      if (!isNaN(Date.parse(firstValue))) {
        return 'date';
      }
      
    }
  }

  return 'text'; // Default
};


/**
 * Get filter summary for display
 * @param {Object} activeFilters - Current active filters
 * @param {Function} t - Translation function
 * @returns {Object} Filter summary with counts and descriptions
 */
export const getFilterSummary = (activeFilters, t) => {
  const summary = {
    totalCount: 0,
    categories: [],
    description: ''
  };

  Object.entries(activeFilters).forEach(([key, values]) => {
    if (values && values.length > 0) {
      summary.totalCount += values.length;
      summary.categories.push({
        key,
        count: values.length,
        label: t ? t(key) : key
      });
    }
  });

  if (summary.totalCount === 0) {
    summary.description = t ? t('noFiltersActive') : 'No filters active';
  } else if (summary.totalCount === 1) {
    summary.description = t ? t('oneFilterActive') : '1 filter active';
  } else {
    summary.description = t ? 
      t('multipleFiltersActive', { count: summary.totalCount }) : 
      `${summary.totalCount} filters active`;
  }

  return summary;
};

export default {
  FILTER_TRANSFORMATIONS,
  transformFilterValues,
  createFilterOptions,
  detectFilterType,
  getFilterSummary
};
